import React from "react";
import type { SVGProps } from "react";

export type CentralIconBaseProps = {
  size?: string | number;
  ariaHidden?: boolean;
  mode?: "masked" | "raw";
  maskId?: string;
} & SVGProps<SVGSVGElement>;

type Props = CentralIconBaseProps & {
  ariaLabel?: string;
  /** Internal generated shapes that contain instance-scoped SVG references. */
  renderShapes?: (instanceId: string) => React.ReactNode;
};
type RenderProps = Props & { svgRef?: React.ForwardedRef<SVGSVGElement> };

const ModernCentralIconBase: React.FC<RenderProps> = (props) => {
  const instanceId = React.useId();
  return <CentralIconSvg {...props} instanceId={instanceId} />;
};

let nextLegacyId = 0;

// React's Server Components runtime exposes useId but not Component. Only
// define the legacy class when it is needed, so modern imports stay RSC-safe.
const LegacyCentralIconBase =
  typeof React.useId === "function"
    ? undefined
    : class extends React.Component<
        { iconProps: RenderProps },
        { instanceId?: string }
      > {
        state: { instanceId?: string } = {};

        componentDidMount() {
          // Defer legacy IDs until mount so server and initial client markup agree.
          this.setState({ instanceId: `legacy-${++nextLegacyId}` });
        }

        render() {
          return (
            <CentralIconSvg
              {...this.props.iconProps}
              instanceId={this.state.instanceId}
            />
          );
        }
      };

export const CentralIconBase = React.forwardRef<SVGSVGElement, Props>(
  (props, ref) =>
    LegacyCentralIconBase ? (
      <LegacyCentralIconBase iconProps={{ ...props, svgRef: ref }} />
    ) : (
      <ModernCentralIconBase {...props} svgRef={ref} />
    ),
);
CentralIconBase.displayName = "CentralIconBase";

const CentralIconSvg: React.FC<RenderProps & { instanceId?: string }> = ({
  children,
  size = 24,
  ariaLabel,
  color,
  ariaHidden,
  "aria-hidden": ariaHiddenAttribute,
  "aria-label": ariaLabelAttribute,
  role,
  svgRef,
  renderShapes,
  style,
  mode = "masked",
  maskId,
  instanceId,
  ...props
}) => {
  const uniqueMaskId = instanceId ? `${maskId}-${instanceId}` : maskId;
  const masked = mode !== "raw" && !!maskId;
  const hidden = ariaHiddenAttribute ?? ariaHidden ?? true;
  const isHidden = hidden === true || hidden === "true";
  const label = ariaLabelAttribute ?? ariaLabel;
  const shapes = renderShapes
    ? renderShapes(uniqueMaskId ?? instanceId ?? "icon")
    : children;
  return (
    <svg
      {...props}
      ref={svgRef}
      aria-hidden={hidden}
      aria-label={ariaLabelAttribute}
      role={role ?? (isHidden ? undefined : "img")}
      width={typeof size === "number" ? `${size}px` : size}
      height={typeof size === "number" ? `${size}px` : size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ color, ...style }}
    >
      {label && !isHidden && <title>{label}</title>}
      {masked ? (
        <>
          <mask
            id={uniqueMaskId}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="24"
            height="24"
          >
            <rect width="24" height="24" fill="#000" />
            <g fill="none" style={{ color: "#fff" }}>
              {shapes}
            </g>
          </mask>
          <rect
            width="24"
            height="24"
            fill="currentColor"
            mask={`url(#${uniqueMaskId})`}
          />
        </>
      ) : (
        shapes
      )}
    </svg>
  );
};
